import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, Users, Link2 } from 'lucide-react';
import { PromoDetailsForm } from '../components/PromoDetailsForm';
import { Button } from "../components/ui/button";
import { useAuth } from '../contexts/AuthContext';
import { usePublish } from '../contexts/PublishContext';
import { ShareModal } from '../components/ShareModal';
import { addPromoToShow, updatePromoForShow, getPromosForShow, deletePromoFromShow, updateShowMetadata, ShowMetaDataType, Promo, PromoDetailsData } from '../services/showService';
import { toast } from 'sonner';
import styles from '../styles/Publish.module.css';
import { usePromoDetails } from '../hooks/useShowDetails';

export default function PromoDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showId } = useParams<{ showId: string }>();
  const { isAuth, loading } = useAuth();
  const { state, updateState } = usePublish();
  const [showShareModal, setShowShareModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState<any>(null);
  // Only call the hook when authentication is ready
  const promoApi = usePromoDetails(!loading && isAuth ? showId || null : null);

  // Sync API data with local state when promos are loaded
  useEffect(() => {
    if (promoApi.promos !== undefined) {
      const newData = {
        promos: promoApi.promos || [],
        successfulPromoLinks: promoApi.successfulLinks || [],
        promoPerformanceDetails: promoApi.promoPerformanceDetails || '',
        outsourcedShow: promoApi.outsourcedShow || null
      };
      updateState({ promoDetails: newData });
      // Store initial data for change comparison
      setInitialData(JSON.stringify(newData));
      setHasChanges(false);
    }
  }, [promoApi.promos, promoApi.successfulLinks, promoApi.promoPerformanceDetails, promoApi.outsourcedShow]);

  // Track changes to promo data
  useEffect(() => {
    if (initialData && state.promoDetails) {
      const currentData = JSON.stringify(state.promoDetails);
      setHasChanges(currentData !== initialData);
    }
  }, [state.promoDetails, initialData]);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!loading && !isAuth) {
      navigate('/', { replace: true });
    }
  }, [isAuth, loading, navigate]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuth) {
    return null;
  }

  const handleSave = async () => {
    if (!showId) {
      toast.error('No show ID available');
      return;
    }

    if (!state.promoDetails) {
      toast.error('No promo data to save');
      return;
    }

    try {
      toast.loading('Saving promo details...', { id: 'save-promo' });

      // Save each promo to the server
      for (const promo of state.promoDetails.promos || []) {
        if (promo.id) {
          // Update existing promo
          await updatePromoForShow(showId, promo.id, promo);
        } else {
          // Add new promo
          await addPromoToShow(showId, promo);
        }
      }
      
      // Save metadata (successful promo links and promo performance details)
      const metadataUpdates = [];

      if (state.promoDetails.successfulPromoLinks.length > 0) {
        metadataUpdates.push({
          type: ShowMetaDataType.SUCCESSFUL_PROMOS,
          value: JSON.stringify(state.promoDetails.successfulPromoLinks)
        });
      }

      if (state.promoDetails.promoPerformanceDetails) {
        metadataUpdates.push({
          type: ShowMetaDataType.PROMO_PERFORMANCE_DETAILS,
          value: state.promoDetails.promoPerformanceDetails
        });
      }

      if (metadataUpdates.length > 0) {
        await updateShowMetadata(showId, metadataUpdates);
      }
      
      // Reset change tracking after successful save
      setInitialData(JSON.stringify(state.promoDetails));
      setHasChanges(false);
      
      toast.success('Promo details saved successfully!', { id: 'save-promo' });
    } catch (error) {
      console.error('Failed to save promo details:', error);
      toast.error('Failed to save promo details. Please try again.', { id: 'save-promo' });
    }
  };

  const handleContinue = async () => {
    // Only auto-save if there are changes
    if (showId && hasChanges && (state.promoDetails.promos.length > 0 || state.promoDetails.successfulPromoLinks.length > 0 || state.promoDetails.promoPerformanceDetails)) {
      try {
        toast.loading('Saving changes...', { id: 'auto-save' });

        // Save each promo to the server
        for (const promo of state.promoDetails.promos) {
          if (promo.id) {
            // Update existing promo
            await updatePromoForShow(showId, promo.id, promo);
          } else {
            // Add new promo
            await addPromoToShow(showId, promo);
          }
        }

        // Save metadata (successful promo links and promo performance details)
        const metadataUpdates = [];

        if (state.promoDetails.successfulPromoLinks.length > 0) {
          metadataUpdates.push({
            type: ShowMetaDataType.SUCCESSFUL_PROMOS,
            value: JSON.stringify(state.promoDetails.successfulPromoLinks)
          });
        }

        if (state.promoDetails.promoPerformanceDetails) {
          metadataUpdates.push({
            type: ShowMetaDataType.PROMO_PERFORMANCE_DETAILS,
            value: state.promoDetails.promoPerformanceDetails
          });
        }

        if (metadataUpdates.length > 0) {
          await updateShowMetadata(showId, metadataUpdates);
        }

        // Reset change tracking after successful save
        setInitialData(JSON.stringify(state.promoDetails));
        setHasChanges(false);

        toast.success('Changes saved successfully!', { id: 'auto-save' });
      } catch (error) {
        console.error('Failed to auto-save promo details:', error);
        toast.error('Failed to save changes', { id: 'auto-save' });
        return; // Don't navigate if save failed
      }
    }
    navigate(`/publish/${showId}/dubbing-details`);
  };

  const handleBack = () => {
    navigate(`/publish/${showId}/internal-details`);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          {/* Left Section */}
          <div className={styles.leftSection}>
            <h1 className={styles.navTitle}>Publish</h1>
          </div>
        
          {/* Center Section */}
          <div className={styles.centerSection}>
            <Button
              variant={location.pathname === `/publish/${showId}` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId}`)}
            >
              Show Details
            </Button>
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/internal-details` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/internal-details`)}
            >
              Internal Details
            </Button>
            {/* Promo Details — hidden per content team request, re-enable when needed */}
            {/* <Button
              variant={location.pathname === `/publish/${showId || 'new-show'}/promo-details` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/promo-details`)}
            >
              Promo Details
            </Button> */}
            {/* Dubbing Details — hidden per content team request, re-enable when needed */}
            {/* <Button
              variant={location.pathname === `/publish/${showId || 'new-show'}/dubbing-details` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/dubbing-details`)}
            >
              Dubbing Details
            </Button> */}
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/thumbnails` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}

              onClick={() => navigate(`/publish/${showId || 'new-show'}/thumbnails`)}
            >
              Thumbnails
            </Button>
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/content` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/content`)}
            >
              Content
            </Button>
            {/* Bonus Content — hidden per content team request, re-enable when needed */}
            {/* <Button
              variant={location.pathname === `/publish/${showId || 'new-show'}/extras` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/extras`)}
            >
              Bonus Content
            </Button> */}
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/preview` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/preview`)}
            >
              Review & Publish
            </Button>
          </div>
        
          {/* Right Section */}
          <div className={styles.rightSection}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
            >
              Save Draft
            </Button>
            <Button
              variant="default"
              onClick={handleContinue}
              size="sm"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {/* Page Title */}
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Promo Details</h2>
            <div className={styles.headerActions}>
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)}>
                <Users className="h-4 w-4 mr-2" />
                Invite
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className={styles.iconButton}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className={styles.sectionSpacing}>
            {promoApi.loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading promo details...</p>
                </div>
              </div>
            ) : (
              <PromoDetailsForm
                data={state.promoDetails || { promos: [], successfulPromoLinks: [], promoPerformanceDetails: '', outsourcedShow: null }}
                onChange={(data) => updateState({ promoDetails: data })}
              />
            )}
            
            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-8">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleContinue}
                className="flex items-center gap-2"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ShareModal open={showShareModal} onOpenChange={setShowShareModal} />
    </div>
  );
}
